import { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { LOGO_URL, WHATSAPP_LINK } from "../data/site";
import { WhatsAppIcon } from "./WhatsAppIcon";

const MAIN_LINKS = [
  { to: "/about", label: "About" },
  { to: "/projects", label: "Projects" },
  { to: "/developers", label: "Developers" },
  { to: "/services", label: "Services" },
  { to: "/investment-guide", label: "Investment" },
];
const MORE_LINKS = [
  { to: "/faq", label: "FAQs" },
  { to: "/reviews", label: "Client Reviews" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = ({ onOpenConsult }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMoreOpen(false); }, [location.pathname]);

  const isActive = (to) => location.pathname === to;

  return (
    <nav
      data-testid="udp-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass-strong py-3" : "glass py-4"}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo-link">
          <img src={LOGO_URL} alt="Universal Dream Properties" className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover ring-1 ring-[#c5a059]/40" />
          <div className="leading-tight hidden sm:block">
            <div className="text-[10px] tracking-[0.35em] text-[#c5a059]">UNIVERSAL DREAM</div>
            <div className="text-[10px] tracking-[0.45em] text-white/60">PROPERTIES LLC</div>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-7 uppercase text-[11px] tracking-[0.22em] font-semibold text-white/80">
          {MAIN_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className={`hover:text-[#c5a059] transition-colors ${isActive(l.to) ? "text-[#c5a059]" : ""}`} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </Link>
          ))}
          <div className="relative" onMouseEnter={() => setMoreOpen(true)} onMouseLeave={() => setMoreOpen(false)}>
            <button className="flex items-center gap-1 hover:text-[#c5a059]" data-testid="nav-more">
              More <ChevronDown size={12} />
            </button>
            {moreOpen && (
              <div className="absolute top-full right-0 pt-3 min-w-[200px]">
                <div className="glass-strong rounded-xl border border-white/10 py-2">
                  {MORE_LINKS.map((l) => (
                    <Link key={l.to} to={l.to} className="block px-5 py-2.5 text-[11px] tracking-[0.2em] hover:text-[#c5a059] hover:bg-white/5" data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button onClick={onOpenConsult} className="pulse-cta-soft hover:text-[#c5a059]" data-testid="nav-consultation">
            Consultation
          </button>
        </div>

        <div className="flex items-center gap-3">
          <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="pulse-cta-wa hidden md:inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[11px] font-bold tracking-[0.2em] bg-[#25D366] text-white hover:opacity-90" data-testid="navbar-whatsapp-btn">
            <WhatsAppIcon size={13} /> WHATSAPP
          </a>
          <button className="lg:hidden text-white p-2" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu" data-testid="navbar-mobile-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass-strong border-t border-white/10 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3 uppercase text-xs tracking-[0.25em]">
            {[...MAIN_LINKS, ...MORE_LINKS].map((l) => (
              <Link key={l.to} to={l.to} className="text-white/80 hover:text-[#c5a059] py-1" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.label}
              </Link>
            ))}
            <button onClick={() => { onOpenConsult?.(); setOpen(false); }} className="text-left text-[#c5a059] py-1" data-testid="mobile-nav-consultation">
              Consultation
            </button>
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="pulse-cta-wa inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-xs font-bold tracking-[0.2em] bg-[#25D366] text-white mt-2">
              <WhatsAppIcon size={14} /> WHATSAPP NOW
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};
